import { ScreenBrightness } from '@capacitor-community/screen-brightness';
import { Capacitor } from '@capacitor/core';
import { addBreadcrumb } from '@sentry/react';

import { logInDev } from '@ocx/utils';

import { MAX_DISPLAY_BRIGHTNESS, MIN_DISPLAY_BRIGHTNESS } from './display-brightness.constants';

import type { DisplayBrightnessValue } from './display-brightness.types';

const logDisplayBrightnessError = (error: unknown) => {
  logInDev(error);
  addBreadcrumb({
    message: '[DisplayBrightnessController] failed to set display brightness',
    data: { error: error instanceof Error ? error.message : String(error) },
  });
};

const resolveDisplayBrightness = (brightness: Exclude<DisplayBrightnessValue, 'initial'>): number => {
  if (brightness === 'min') {
    return MIN_DISPLAY_BRIGHTNESS;
  }

  if (brightness === 'max') {
    return MAX_DISPLAY_BRIGHTNESS;
  }

  if (!Number.isFinite(brightness)) {
    throw new Error('[DisplayBrightnessController] brightness value must be finite');
  }

  return Math.min(Math.max(brightness, MIN_DISPLAY_BRIGHTNESS), MAX_DISPLAY_BRIGHTNESS);
};

class DisplayBrightnessController {
  private initialBrightness: number | null = null;

  private operation = Promise.resolve();

  setBrightness(brightness: DisplayBrightnessValue): void {
    this.operation = this.operation.then(() => this.setBrightnessInternal(brightness)).catch(logDisplayBrightnessError);
  }

  private async setBrightnessInternal(brightness: DisplayBrightnessValue): Promise<void> {
    if (!Capacitor.isNativePlatform()) {
      return;
    }

    if (brightness === 'initial') {
      if (this.initialBrightness === null) {
        return;
      }

      await ScreenBrightness.setBrightness({ brightness: this.initialBrightness });
      this.initialBrightness = null;
      return;
    }

    if (this.initialBrightness === null) {
      const { brightness: currentBrightness } = await ScreenBrightness.getBrightness();
      this.initialBrightness = currentBrightness;
    }

    await ScreenBrightness.setBrightness({ brightness: resolveDisplayBrightness(brightness) });
  }
}

export const displayBrightnessController = new DisplayBrightnessController();
